<template>
  <section class="apps-section users-section">
    <div class="section-header admin-header">
      <div>
        <p class="section-label">Administração</p>
        <h3>Users</h3>
      </div>

      <div class="admin-header-actions flex-grow-1 justify-end">
        <v-text-field
          v-model="search"
          label="Pesquisar"
          density="compact"
          variant="outlined"
          hide-details
          clearable
          style="max-width: 280px;"
          @update:model-value="onSearchChange"
        />

        <v-btn
          color="primary"
          variant="tonal"
          @click="importDialog = true"
        >
          <v-icon start>mdi-upload</v-icon>
          Importar users
        </v-btn>

        <v-btn
          color="primary"
          icon
          @click="openCreateUserDialog"
        >
          <v-icon>mdi-plus</v-icon>
        </v-btn>
      </div>
    </div>

    <v-card class="admin-card" elevation="0">
      <v-data-table-server
        v-model:items-per-page="itemsPerPage"
        v-model:page="page"
        :headers="headers"
        :items="usersList"
        :items-length="totalUsers"
        :loading="loading"
        :items-per-page-options="[5, 10, 15, 20]"
        item-value="id"
        @update:options="loadUsersList"
      >
        <template #item.is_active="{ item }">
          <v-chip
            :color="item.is_active ? 'success' : 'error'"
            size="small"
            variant="flat"
          >
            {{ item.is_active ? 'Ativo' : 'Inativo' }}
          </v-chip>
        </template>

        <template #item.is_external="{ item }">
          <v-chip
            :color="item.is_external ? 'warning' : 'info'"
            size="small"
            variant="flat"
          >
            {{ item.is_external ? 'Externo' : 'Interno' }}
          </v-chip>
        </template>

        <template #item.created_at="{ item }">
          {{ formatDate(item.created_at) }}
        </template>

        <template #item.updated_at="{ item }">
          {{ formatDate(item.updated_at) }}
        </template>

        <template #item.actions="{ item }">
          <div class="row-actions">
            <v-tooltip
              text="Gerir permissões das aplicações"
              location="top"
            >
              <template #activator="{ props }">
                <v-btn
                  v-bind="props"
                  icon
                  variant="text"
                  @click="openPermissionsDialog(item.id)"
                >
                  <v-icon>mdi-shield-key</v-icon>
                </v-btn>
              </template>
            </v-tooltip>

            <v-btn
              icon
              variant="text"
              @click="openEditUserDialog(item.id)"
            >
              <v-icon>mdi-eye</v-icon>
            </v-btn>

            <ui-icon-remove
              @on-submit="removeItem(item.id)"
            />
          </div>
        </template>

        <template #no-data>
          <div class="empty-state">
            <div class="empty-icon">i</div>

            <h4>Sem resultados</h4>

            <p>Nenhum user encontrado.</p>
          </div>
        </template>
      </v-data-table-server>
    </v-card>

    <v-dialog
      v-model="dialog"
      max-width="960"
    >
      <v-card class="dialog-card">
        <v-card-title>
          {{ isEditing ? 'Editar user' : 'Criar user' }}
        </v-card-title>

        <v-card-text>
          <v-row>
            <v-col
              cols="12"
              md="6"
            >
              <v-text-field
                v-model="form.name"
                label="Nome *"
                variant="outlined"
                density="comfortable"
                :error-messages="errors.name"
                :loading="fetchingUser"
                :disabled="fetchingUser || submitting"
              />
            </v-col>

            <v-col
              cols="12"
              md="6"
            >
              <v-text-field
                v-model="form.username"
                label="Username *"
                variant="outlined"
                density="comfortable"
                :readonly="isEditing"
                :error-messages="errors.username"
                :loading="fetchingUser"
                :disabled="fetchingUser || submitting"
              />
            </v-col>

            <v-col
              cols="12"
              md="6"
            >
              <v-text-field
                v-model="form.email"
                label="Email *"
                variant="outlined"
                density="comfortable"
                :readonly="isEditing"
                :error-messages="errors.email"
                :loading="fetchingUser"
                :disabled="fetchingUser || submitting"
              />
            </v-col>

            <v-col
              cols="12"
              md="6"
            >
              <v-text-field
                v-model="form.telephone"
                label="Telefone"
                variant="outlined"
                density="comfortable"
                :error-messages="errors.telephone"
                :loading="fetchingUser"
                :disabled="fetchingUser || submitting"
              />
            </v-col>

            <v-col cols="12">
              <v-text-field
                v-model="form.password"
                label="Password"
                type="password"
                variant="outlined"
                density="comfortable"
                :error-messages="errors.password"
                :hint="isEditing ? 'Preencher apenas se quiser alterar' : 'Opcional'"
                :persistent-hint="true"
                :loading="fetchingUser"
                :disabled="fetchingUser || submitting"
              />
            </v-col>

            <v-col
              cols="12"
              md="6"
            >
              <v-autocomplete
                v-model="form.company"
                :items="companies"
                item-title="name"
                item-value="name"
                label="Empresa *"
                variant="outlined"
                density="comfortable"
                :clearable="!isCompanyReadOnly"
                :loading="companiesLoading || fetchingUser"
                :disabled="companiesLoading || fetchingUser || submitting"
                :readonly="isCompanyReadOnly"
                :menu-icon="isCompanyReadOnly ? '' : '$dropdown'"
                :error-messages="errors.company"
                @update:model-value="onCompanyChange"
              />
            </v-col>

            <v-col
              cols="12"
              md="6"
            >
              <v-autocomplete
                v-model="form.funcionario_codigo"
                :items="filteredWorkers"
                :item-title="workerLabel"
                item-value="code"
                label="Código de funcionário *"
                variant="outlined"
                density="comfortable"
                :clearable="!isEditing"
                :disabled="workersLoading || fetchingUser || submitting"
                :loading="workersLoading || fetchingUser"
                :error-messages="errors.funcionario_codigo"
                :readonly="isEditing && !!form.funcionario_codigo"
                :menu-icon="(isEditing && !!form.funcionario_codigo) ? '' : '$dropdown'"
                @update:model-value="onWorkerChange"
              />
            </v-col>

            <v-col
              cols="12"
              md="6"
            >
              <v-switch
                v-model="form.is_external"
                label="Externo"
                color="#4CAF50"
                inset
                hide-details
                :loading="fetchingUser"
                :disabled="fetchingUser || submitting"
              />
            </v-col>

            <v-col
              cols="12"
              md="6"
            >
              <v-switch
                v-model="form.is_active"
                label="Ativo"
                color="#4CAF50"
                inset
                hide-details
                :loading="fetchingUser"
                :disabled="fetchingUser || submitting"
              />
            </v-col>

            <v-col
              cols="12"
              md="6"
            >
              <v-autocomplete
                v-model="form.central_cost_id"
                :items="centralCosts"
                :item-title="centralCostLabel"
                item-value="id"
                label="Base de trabalho"
                variant="outlined"
                density="comfortable"
                readonly
                :loading="centralCostsLoading || fetchingUser"
                :disabled="fetchingUser"
                :menu-icon="''"
                :error-messages="errors.central_cost_id"
              />
            </v-col>

            <v-col
              cols="12"
              md="6"
            >
              <v-autocomplete
                v-model="form.central_cost_ids"
                :items="centralCosts"
                :item-title="centralCostLabel"
                item-value="id"
                label="Centros de custo"
                variant="outlined"
                density="comfortable"
                chips
                closable-chips
                multiple
                clearable
                :loading="centralCostsLoading || fetchingUser"
                :disabled="fetchingUser"
                :error-messages="errors.central_cost_ids"
              />
            </v-col>
          </v-row>

          <div
            v-if="submitError"
            class="submit-error"
          >
            {{ submitError }}
          </div>
        </v-card-text>

        <v-card-actions class="justify-end">
          <v-btn
            variant="text"
            :disabled="submitting"
            @click="closeDialog"
          >
            Cancelar
          </v-btn>

          <v-btn
            color="primary"
            :loading="submitting"
            :disabled="submitting || fetchingUser"
            @click="submitForm"
          >
            {{ isEditing ? 'Guardar' : 'Criar' }}
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <v-dialog
      v-model="permissionsDialog"
      max-width="860"
    >
      <v-card class="dialog-card">
        <v-card-title>
          Permissões de aplicações
        </v-card-title>

        <v-card-text>
          <div
            v-if="permissionsLoading"
            class="py-6 text-center"
          >
            A carregar permissões...

            <v-skeleton-loader
              type="list-item-avatar-two-line"
              boilerplate
            />
          </div>

          <div v-else>
            <div
              v-if="permissionsItems.length === 0"
              class="empty-state"
            >
              <div class="empty-icon">i</div>

              <h4>Sem aplicações</h4>

              <p>Não existem aplicações disponíveis.</p>
            </div>

            <v-list
              v-else
              class="permissions-list"
            >
              <v-list-item
                v-for="permission in permissionsItems"
                :key="permission.id"
                class="permission-item"
                :class="{
                  'permission-item--olympus': isOlympusPermission(permission)
                }"
              >
                <div class="permission-header">
                  <div class="permission-main">
                    <div
                      class="permission-information"
                      :class="{
                        'cursor-pointer': !isOlympusPermission(permission)
                      }"
                      @click="togglePermissionFromLabel(permission)"
                    >
                      <div class="permission-title-row">
                        <v-list-item-title>
                          {{ permission.name }}
                        </v-list-item-title>

                        <v-chip
                          v-if="isOlympusPermission(permission)"
                          color="success"
                          size="x-small"
                          variant="tonal"
                        >
                          Aplicação principal
                        </v-chip>
                      </div>

                      <v-list-item-subtitle>
                        {{ permission.role }}
                      </v-list-item-subtitle>
                    </div>
                  </div>

                  <div
                    v-if="isOlympusPermission(permission)"
                    class="administrator-option"
                  >
                    <v-checkbox
                      v-model="permission.is_admin"
                      color="#4CAF50"
                      label="Nomear administrador"
                      hide-details
                      density="comfortable"
                    />
                  </div>

                  <div
                    v-else
                    class="permission-switch-option"
                  >
                    <v-switch
                      :model-value="permission.enabled"
                      color="#4CAF50"
                      inset
                      hide-details
                      density="comfortable"
                      :label="permission.enabled ? 'Ativo' : 'Inativo'"
                      @update:model-value="
                        onPermissionToggle(
                          permission,
                          $event
                        )
                      "
                    />
                  </div>
                </div>

                <v-row
                  v-if="
                    permission.enabled &&
                      !isOlympusPermission(permission)
                  "
                  class="ma-0 justify-end permission-roles-row"
                >
                  <v-col cols="12">
                    <v-autocomplete
                      v-model="permission.roles"
                      variant="outlined"
                      clearable
                      chips
                      closable-chips
                      multiple
                      label="Roles dentro da aplicação"
                      :items="appSpecificRolesList[permission.id] || []"
                      item-title="display_name"
                      item-value="id"
                      hide-details
                      :disabled="fetchingAppRoles?.[permission.id]"
                      :loading="fetchingAppRoles?.[permission.id]"
                      autocomplete="off"
                    />
                  </v-col>
                </v-row>
              </v-list-item>
            </v-list>

            <div
              v-if="permissionsSubmitError"
              class="submit-error"
            >
              {{ permissionsSubmitError }}
            </div>
          </div>
        </v-card-text>

        <v-card-actions class="justify-end">
          <v-btn
            variant="text"
            :disabled="permissionsSubmitting"
            @click="closePermissionsDialog"
          >
            Cancelar
          </v-btn>

          <v-btn
            color="primary"
            :loading="permissionsSubmitting"
            :disabled="permissionsLoading || permissionsSubmitting"
            @click="submitPermissions"
          >
            Guardar permissões
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <ImportUsers
      v-model:opened="importDialog"
      @import="handleImportUsers"
    />
  </section>
</template>

<script>
import Users from '@/api/Users'
import Company from '@/api/Company'
import Workers from '@/api/Workers'
import UiIconRemove from '@/components/ui/IconRemove.vue'
import ImportUsers from '@/components/Users/Importer/ImportUsers.vue'
import AccessRoles from '@/api/AccessRoles'
import CentralCosts from '@/api/CentralCosts'

export default {
  name: 'UsersAdminView',

  components: {
    UiIconRemove,
    ImportUsers
  },

  data() {
    return {
      loading: false,
      submitting: false,
      dialog: false,
      importDialog: false,
      isEditing: false,
      editingId: null,
      usersList: [],
      workers: [],
      workersLoading: false,
      totalUsers: 0,
      page: 1,
      itemsPerPage: 10,
      search: '',
      submitError: '',

      companies: [],
      companiesLoading: false,
      centralCosts: [],
      centralCostsLoading: false,

      permissionsDialog: false,
      permissionsLoading: false,
      permissionsSubmitting: false,
      permissionsSubmitError: '',
      permissionsUserId: null,
      permissionsItems: [],

      form: {
        name: '',
        email: '',
        username: '',
        telephone: '',
        funcionario_codigo: '',
        company: '',
        central_cost_id: null,
        central_cost_ids: [],
        password: '',
        is_active: true,
        is_external: false
      },

      errors: {
        name: '',
        email: '',
        username: '',
        telephone: '',
        funcionario_codigo: '',
        company: '',
        central_cost_id: '',
        central_cost_ids: '',
        password: ''
      },

      appSpecificRolesList: {},
      fetchingAppRoles: {},
      fetchedAppRoles: {},
      fetchingUser: false,

      headers: [
        {
          title: 'Nome',
          key: 'name'
        },
        {
          title: 'Email',
          key: 'email'
        },
        {
          title: 'Username',
          key: 'username'
        },
        {
          title: 'Empresa',
          key: 'company'
        },
        {
          title: 'Ativo',
          key: 'is_active'
        },
        {
          title: 'Externo',
          key: 'is_external'
        },
        {
          title: 'Criado em',
          key: 'created_at'
        },
        {
          title: 'Atualizado em',
          key: 'updated_at'
        },
        {
          title: '',
          key: 'actions',
          sortable: false,
          width: 160
        }
      ]
    }
  },

  computed: {
    filteredWorkers() {
      const selectedCompany = this.findCompanyByName(
        this.form.company
      )

      return this.workers.filter((worker) => {
        const isActive =
          worker.is_active === true ||
          worker.is_active === 1 ||
          worker.is_active === '1'

        if (!isActive) {
          return false
        }

        if (!selectedCompany?.id) {
          return true
        }

        return worker.company_id === selectedCompany.id
      })
    },

    isWorkerSelectionLocked() {
      return (
        this.isEditing &&
        !!this.form.funcionario_codigo
      )
    },

    isCompanyReadOnly() {
      return (
        this.isEditing ||
        !!this.form.funcionario_codigo
      )
    }
  },

  mounted() {
    this.loadCompanies()
    this.loadCentralCosts()
  },

  methods: {
    defaultForm() {
      return {
        name: '',
        email: '',
        username: '',
        telephone: '',
        funcionario_codigo: '',
        company: '',
        central_cost_id: null,
        central_cost_ids: [],
        password: '',
        is_active: true,
        is_external: false
      }
    },

    defaultErrors() {
      return {
        name: '',
        email: '',
        username: '',
        telephone: '',
        funcionario_codigo: '',
        company: '',
        central_cost_id: '',
        central_cost_ids: '',
        password: ''
      }
    },

    createOlympusPermission(payload = {}) {
      return {
        id: '__olympus__',
        name: 'Olympus',
        display_name: 'Olympus',
        role: 'Acesso à plataforma principal',
        enabled: true,
        roles: [],
        is_olympus: true,
        is_admin: !!(
          payload.is_admin ??
          payload.user?.is_admin ??
          payload.user?.admin ??
          false
        )
      }
    },

    isOlympusPermission(permission) {
      return (
        permission?.is_olympus === true ||
        permission?.id === '__olympus__'
      )
    },

    normalizePermissionsItems(items) {
      if (!Array.isArray(items)) {
        return []
      }

      return items.map((permission) => {
        const isOlympus =
          this.isOlympusPermission(permission)

        return {
          ...permission,

          enabled: isOlympus
            ? true
            : !!permission.enabled,

          is_admin: isOlympus
            ? !!permission.is_admin
            : false,

          roles: Array.isArray(permission.roles)
            ? [...permission.roles]
            : []
        }
      })
    },

    normalizeId(value) {
      return value
        ? String(value).trim()
        : null
    },

    getUserCentralCostIds(item) {
      const ids = []

      if (Array.isArray(item?.central_cost_ids)) {
        item.central_cost_ids.forEach((id) => {
          const normalizedId =
            this.normalizeId(id)

          if (normalizedId) {
            ids.push(normalizedId)
          }
        })
      }

      if (Array.isArray(item?.central_costs)) {
        item.central_costs.forEach((centralCost) => {
          const normalizedId =
            this.normalizeId(centralCost?.id)

          if (normalizedId) {
            ids.push(normalizedId)
          }
        })
      }

      const directCentralCostId =
        this.normalizeId(item?.central_cost_id)

      if (directCentralCostId) {
        ids.push(directCentralCostId)
      }

      return [...new Set(ids)]
    },

    findCompanyByName(name) {
      if (!name) {
        return null
      }

      return this.companies.find((company) => {
        return company.name === name
      }) || null
    },

    findCompanyById(companyId) {
      if (!companyId) {
        return null
      }

      return this.companies.find((company) => {
        return company.id === companyId
      }) || null
    },

    findWorkerByCode(code) {
      if (!code) {
        return null
      }

      return this.workers.find((worker) => {
        return worker.code === code
      }) || null
    },

    clearWorkerDerivedFields(
      clearCentralCostIds = true
    ) {
      this.form.central_cost_id = null

      if (clearCentralCostIds) {
        this.form.central_cost_ids = []
      }
    },

    applyWorkerSelection(
      workerCode,
      syncCompany = true,
      syncCentralCostIds = true
    ) {
      const worker =
        this.findWorkerByCode(workerCode)

      if (!worker) {
        if (syncCentralCostIds) {
          this.clearWorkerDerivedFields(true)
        }

        return
      }

      if (
        syncCompany &&
        worker.company_id
      ) {
        const company =
          this.findCompanyById(worker.company_id)

        this.form.company =
          company?.name ||
          worker.company_name ||
          this.form.company ||
          ''
      }

      this.form.central_cost_id =
        worker.central_cost_id ||
        this.form.central_cost_id ||
        null

      if (syncCentralCostIds) {
        const workerCentralCostIds =
          Array.isArray(worker.user_central_cost_ids)
            ? worker.user_central_cost_ids
                .map((id) => this.normalizeId(id))
                .filter(Boolean)
            : []

        if (workerCentralCostIds.length > 0) {
          this.form.central_cost_ids = [
            ...new Set(workerCentralCostIds)
          ]
        } else if (
          worker.central_cost_id &&
          this.form.central_cost_ids.length === 0
        ) {
          this.form.central_cost_ids = [
            worker.central_cost_id
          ]
        }
      }
    },

    onCompanyChange(companyName) {
      const selectedWorker =
        this.findWorkerByCode(
          this.form.funcionario_codigo
        )

      if (!companyName) {
        this.form.funcionario_codigo = ''
        this.clearWorkerDerivedFields()
        return
      }

      if (!selectedWorker) {
        return
      }

      const selectedCompany =
        this.findCompanyByName(companyName)

      if (
        selectedCompany?.id &&
        selectedWorker.company_id !== selectedCompany.id
      ) {
        this.form.funcionario_codigo = ''
        this.clearWorkerDerivedFields()
      }
    },

    onWorkerChange(workerCode) {
      if (!workerCode) {
        this.clearWorkerDerivedFields()
        return
      }

      this.applyWorkerSelection(
        workerCode,
        true,
        true
      )
    },

    workerLabel(item) {
      if (!item) {
        return ''
      }

      return `${item.code} - ${item.name}`
    },

    centralCostLabel(item) {
      if (!item) {
        return ''
      }

      if (!item.department) {
        return item.central_cost || ''
      }

      return `${item.central_cost} - ${item.department}`
    },

    async loadWorkers() {
      this.workersLoading = true

      try {
        const response =
          await Workers.getAll()

        this.workers =
          response?.data?.data ||
          response?.data ||
          []

        if (
          this.form.funcionario_codigo &&
          !this.isEditing
        ) {
          this.applyWorkerSelection(
            this.form.funcionario_codigo,
            true,
            true
          )
        }
      } catch (error) {
        console.error(
          'Erro ao carregar workers:',
          error?.response?.data || error
        )

        this.workers = []
      } finally {
        this.workersLoading = false
      }
    },

    async loadCompanies() {
      this.companiesLoading = true

      try {
        const response =
          await Company.getAll()

        const data =
          response?.data?.data ||
          response?.data ||
          []

        this.companies = Array.isArray(data)
          ? data.map((company) => ({
              id: company.id,
              name: company.name
            }))
          : []

        if (
          this.companies.length === 1 &&
          !this.form.company
        ) {
          this.form.company =
            this.companies[0].name
        }
      } catch (error) {
        console.error(
          'Erro ao carregar empresas:',
          error?.response?.data || error
        )

        this.companies = []
      } finally {
        this.companiesLoading = false
      }
    },

    async loadCentralCosts() {
      this.centralCostsLoading = true

      try {
        const response =
          await CentralCosts.search({
            remove_pagination: 1
          })

        const data =
          response?.data?.data ||
          response?.data ||
          []

        this.centralCosts = Array.isArray(data)
          ? data.map((centralCost) => ({
              ...centralCost,
              id: this.normalizeId(centralCost.id)
            }))
          : []
      } catch (error) {
        console.error(
          'Erro ao carregar centros de custo:',
          error?.response?.data || error
        )

        this.centralCosts = []
      } finally {
        this.centralCostsLoading = false
      }
    },

    async ensureWorkersLoaded() {
      if (this.workers.length > 0) {
        return
      }

      if (this.workersLoading) {
        while (this.workersLoading) {
          await new Promise((resolve) => {
            setTimeout(resolve, 50)
          })
        }

        return
      }

      await this.loadWorkers()
    },

    async ensureCompaniesLoaded() {
      if (this.companies.length > 0) {
        return
      }

      if (this.companiesLoading) {
        while (this.companiesLoading) {
          await new Promise((resolve) => {
            setTimeout(resolve, 50)
          })
        }

        return
      }

      await this.loadCompanies()
    },

    async ensureCentralCostsLoaded() {
      if (this.centralCosts.length > 0) {
        return
      }

      if (this.centralCostsLoading) {
        while (this.centralCostsLoading) {
          await new Promise((resolve) => {
            setTimeout(resolve, 50)
          })
        }

        return
      }

      await this.loadCentralCosts()
    },

    async loadAppRoles(appId, params = {}) {
      if (appId === '__olympus__') {
        return
      }

      if (
        this.fetchedAppRoles?.[appId] &&
        this.appSpecificRolesList?.[appId]?.length > 0
      ) {
        return
      }

      this.fetchingAppRoles[appId] = true

      try {
        const response =
          await AccessRoles.list(appId, params)

        this.appSpecificRolesList[appId] =
          Array.isArray(response?.data?.data)
            ? response.data.data
            : Array.isArray(response?.data)
              ? response.data
              : []

        this.fetchedAppRoles[appId] = true
      } catch (error) {
        console.error(
          'Erro a obter os roles da aplicação:',
          error
        )

        this.fetchedAppRoles[appId] = false
        this.appSpecificRolesList[appId] = []
      } finally {
        this.fetchingAppRoles[appId] = false
      }
    },

    togglePermissionFromLabel(permission) {
      if (this.isOlympusPermission(permission)) {
        permission.enabled = true
        return
      }

      this.onPermissionToggle(
        permission,
        !permission.enabled
      )
    },

    async onPermissionToggle(
      permission,
      enabled
    ) {
      if (this.isOlympusPermission(permission)) {
        permission.enabled = true
        return
      }

      permission.enabled = !!enabled

      if (!permission.enabled) {
        const permissionIndex =
          this.permissionsItems.findIndex((item) => {
            return item.id == permission.id
          })

        if (permissionIndex >= 0) {
          this.permissionsItems[
            permissionIndex
          ].roles = []
        }

        return
      }

      if (!Array.isArray(permission.roles)) {
        permission.roles = []
      }

      await this.loadAppRoles(permission.id)
    },

    async loadUsersList(options = {}) {
      this.loading = true

      try {
        const page =
          options.page ||
          this.page

        const itemsPerPage =
          options.itemsPerPage ||
          this.itemsPerPage

        this.page = page
        this.itemsPerPage = itemsPerPage

        const response =
          await Users.search({
            page: this.page,
            limit: this.itemsPerPage,
            search: this.search
          })

        this.usersList =
          response.data.data ||
          []

        this.totalUsers =
          response.data.total ||
          0
      } catch (error) {
        console.error(
          'Erro ao carregar users:',
          error?.response?.data || error
        )
      } finally {
        this.loading = false
      }
    },

    async handleImportUsers(payload) {
      console.log(
        'Importação concluída:',
        payload
      )

      this.importDialog = false

      await this.loadUsersList({
        page: this.page,
        itemsPerPage: this.itemsPerPage
      })
    },

    onSearchChange() {
      this.page = 1

      this.loadUsersList({
        page: 1,
        itemsPerPage: this.itemsPerPage
      })
    },

    async openCreateUserDialog() {
      this.isEditing = false
      this.editingId = null
      this.submitError = ''
      this.resetErrors()
      this.form = this.defaultForm()
      this.dialog = true

      await Promise.all([
        this.ensureCompaniesLoaded(),
        this.ensureCentralCostsLoaded(),
        this.ensureWorkersLoaded()
      ])
    },

    async openEditUserDialog(id) {
      this.dialog = true
      this.isEditing = true
      this.editingId = id
      this.submitError = ''
      this.resetErrors()
      this.form = this.defaultForm()
      this.fetchingUser = true

      try {
        const [response] =
          await Promise.all([
            Users.get(id),
            this.ensureCompaniesLoaded(),
            this.ensureCentralCostsLoaded(),
            this.ensureWorkersLoaded()
          ])

        const item =
          response.data

        const centralCostIds =
          this.getUserCentralCostIds(item)

        this.form = {
          ...this.defaultForm(),

          name:
            item.name ||
            '',

          email:
            item.email ||
            '',

          username:
            item.username ||
            '',

          telephone:
            item.telephone ||
            '',

          funcionario_codigo:
            item.funcionario_codigo ||
            '',

          company:
            item.company ||
            '',

          central_cost_id:
            item.central_cost_id ||
            item.worker?.central_cost_id ||
            null,

          central_cost_ids:
            centralCostIds,

          password:
            '',

          is_active:
            !!item.is_active,

          is_external:
            !!item.is_external
        }

        if (this.form.funcionario_codigo) {
          this.applyWorkerSelection(
            this.form.funcionario_codigo,
            false,
            false
          )
        }
      } catch (error) {
        console.error(
          'Erro ao obter user:',
          error?.response?.data || error
        )
      } finally {
        this.fetchingUser = false
      }
    },

    async removeItem(id) {
      try {
        await Users.delete(id)

        await this.loadUsersList({
          page: this.page,
          itemsPerPage: this.itemsPerPage
        })
      } catch (error) {
        console.error(
          'Erro ao remover user:',
          error?.response?.data || error
        )
      }
    },

    closeDialog() {
      this.dialog = false
      this.submitError = ''
      this.form = this.defaultForm()
      this.resetErrors()
    },

    resetErrors() {
      this.errors =
        this.defaultErrors()
    },

    validateForm() {
      this.resetErrors()

      let valid = true

      if (!this.form.name?.trim()) {
        this.errors.name =
          'O nome é obrigatório.'

        valid = false
      }

      if (!this.form.email?.trim()) {
        this.errors.email =
          'O email é obrigatório.'

        valid = false
      }

      if (!this.form.company?.trim()) {
        this.errors.company =
          'A empresa é obrigatória.'

        valid = false
      }

      if (!this.form.funcionario_codigo?.trim()) {
        this.errors.funcionario_codigo =
          'O código de funcionário é obrigatório.'

        valid = false
      }

      return valid
    },

    async submitForm() {
      if (!this.validateForm()) {
        return
      }

      this.submitting = true
      this.submitError = ''

      try {
        const payload = {
          name:
            this.form.name.trim(),

          email:
            this.form.email.trim(),

          username:
            this.form.username?.trim() ||
            null,

          telephone:
            this.form.telephone?.trim() ||
            null,

          funcionario_codigo:
            this.form.funcionario_codigo?.trim() ||
            null,

          company:
            this.form.company?.trim(),

          create_or_update_worker_profile:
            !!this.form.create_or_update_worker_profile,

          central_cost_id:
            this.form.central_cost_id ||
            null,

          central_cost_ids:
            Array.isArray(this.form.central_cost_ids)
              ? this.form.central_cost_ids
              : [],

          microsoft_identifier:
            this.form.microsoft_identifier?.trim() ||
            null,

          is_active:
            !!this.form.is_active,

          is_external:
            !!this.form.is_external
        }

        if (this.form.password?.trim()) {
          payload.password =
            this.form.password.trim()
        }

        if (
          this.isEditing &&
          this.editingId
        ) {
          await Users.update(
            payload,
            this.editingId
          )
        } else {
          await Users.create(payload)
        }

        await Promise.all([
          this.loadUsersList({
            page: this.page,
            itemsPerPage: this.itemsPerPage
          }),

          this.loadWorkers()
        ])

        this.dialog = false
      } catch (error) {
        const responseData =
          error?.response?.data

        if (responseData?.errors) {
          this.errors.name =
            responseData.errors.name?.[0] ||
            ''

          this.errors.email =
            responseData.errors.email?.[0] ||
            ''

          this.errors.username =
            responseData.errors.username?.[0] ||
            ''

          this.errors.telephone =
            responseData.errors.telephone?.[0] ||
            ''

          this.errors.funcionario_codigo =
            responseData.errors.funcionario_codigo?.[0] ||
            ''

          this.errors.company =
            responseData.errors.company?.[0] ||
            ''

          this.errors.create_or_update_worker_profile =
            responseData.errors.create_or_update_worker_profile?.[0] ||
            ''

          this.errors.central_cost_id =
            responseData.errors.central_cost_id?.[0] ||
            ''

          this.errors.central_cost_ids =
            responseData.errors.central_cost_ids?.[0] ||
            responseData.errors['central_cost_ids.0']?.[0] ||
            ''

          this.errors.microsoft_identifier =
            responseData.errors.microsoft_identifier?.[0] ||
            ''

          this.errors.password =
            responseData.errors.password?.[0] ||
            ''
        } else {
          this.submitError =
            responseData?.message ||
            'Ocorreu um erro ao guardar.'
        }

        console.error(
          'Erro ao guardar user:',
          responseData || error
        )
      } finally {
        this.submitting = false
      }
    },

    async openPermissionsDialog(id) {
      this.permissionsDialog = true
      this.permissionsLoading = true
      this.permissionsSubmitting = false
      this.permissionsSubmitError = ''
      this.permissionsUserId = id
      this.permissionsItems = []

      this.appSpecificRolesList = {}
      this.fetchingAppRoles = {}
      this.fetchedAppRoles = {}

      try {
        const response =
          await Users.getPermissions(id)

        const payload =
          response.data?.data ||
          {}

        const backendPermissions =
          Array.isArray(payload.permissions)
            ? payload.permissions
            : []

        this.permissionsItems =
          this.normalizePermissionsItems([
            this.createOlympusPermission(payload),
            ...backendPermissions
          ])

        const enabledBackendPermissions =
          this.permissionsItems.filter((permission) => {
            return (
              permission.enabled &&
              !this.isOlympusPermission(permission)
            )
          })

        await Promise.all(
          enabledBackendPermissions.map((permission) => {
            return this.loadAppRoles(permission.id)
          })
        )
      } catch (error) {
        const responseData =
          error?.response?.data

        this.permissionsSubmitError =
          responseData?.message ||
          'Erro ao carregar permissões.'

        console.error(
          'Erro ao carregar permissões do user:',
          responseData || error
        )
      } finally {
        this.permissionsLoading = false
      }
    },

    closePermissionsDialog() {
      this.permissionsDialog = false
      this.permissionsLoading = false
      this.permissionsSubmitting = false
      this.permissionsSubmitError = ''
      this.permissionsUserId = null
      this.permissionsItems = []

      this.appSpecificRolesList = {}
      this.fetchingAppRoles = {}
      this.fetchedAppRoles = {}
    },

    async submitPermissions() {
      if (!this.permissionsUserId) {
        return
      }

      this.permissionsSubmitting = true
      this.permissionsSubmitError = ''

      try {
        const roles = {}
        const enabledAccessIds = []

        const olympusPermission =
          this.permissionsItems.find((permission) => {
            return this.isOlympusPermission(permission)
          })

        this.permissionsItems.forEach((appAccess) => {
          if (this.isOlympusPermission(appAccess)) {
            return
          }

          /*
          * O estado do switch é enviado independentemente
          * de existirem roles selecionadas.
          */
          if (appAccess.enabled) {
            enabledAccessIds.push(appAccess.id)
          }

          /*
          * As roles são enviadas separadamente.
          */
          roles[appAccess.id] =
            appAccess.enabled &&
            Array.isArray(appAccess.roles)
              ? [...appAccess.roles]
              : []
        })

        const payload = {
          roles,
          enabled_access_ids: enabledAccessIds,
          is_admin: !!olympusPermission?.is_admin
        }

        console.log(
          'Payload de atualização de permissões:',
          payload
        )

        await Users.updatePermissions(
          this.permissionsUserId,
          payload
        )

        await this.loadUsersList({
          page: this.page,
          itemsPerPage: this.itemsPerPage
        })

        this.permissionsDialog = false
      } catch (error) {
        const responseData =
          error?.response?.data

        this.permissionsSubmitError =
          responseData?.message ||
          'Erro ao guardar permissões.'

        console.error(
          'Erro ao atualizar permissões do user:',
          responseData || error
        )
      } finally {
        this.permissionsSubmitting = false
      }
    },

    formatDate(value) {
      if (!value) {
        return ''
      }

      return new Date(value).toLocaleString(
        'pt-PT'
      )
    }
  },

  watch: {}
}
</script>

<style scoped>
.users-section {
  margin-top: 32px;
}

.admin-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
}

.admin-header-actions {
  display: flex;
  align-items: center;
  gap: 12px;
}

.admin-card {
  background: #ffffff !important;
  border-radius: 16px;
  padding: 16px;
  box-shadow: none !important;
}

.dialog-card {
  background: #ffffff !important;
  border-radius: 18px;
}

.submit-error {
  margin-top: 12px;
  color: #dc2626;
  font-size: 0.95rem;
}

.empty-state {
  text-align: center;
  padding: 40px;
}

.empty-icon {
  width: 40px;
  height: 40px;
  background: #e2e8f0;
  border-radius: 50%;
  margin: 0 auto 10px;
  display: flex;
  align-items: center;
  justify-content: center;
}

.row-actions {
  display: flex;
  align-items: center;
  gap: 4px;
}

.permissions-list {
  background: #ffffff !important;
}

.permission-item {
  display: block;
  background: #ffffff !important;
  border: 1px solid #e2e8f0;
  border-radius: 12px;
  margin-bottom: 10px;
  padding: 20px 18px;
  box-shadow: none !important;
}

.permission-item--olympus {
  border-color: #86efac;
  background: #f0fdf4 !important;
}

.permission-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 24px;
  width: 100%;
}

.permission-main {
  display: flex;
  align-items: center;
  min-width: 0;
  flex: 1;
}

.permission-information {
  display: flex;
  flex-direction: column;
  min-width: 0;
  margin-left: 12px;
}

.permission-title-row {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 8px;
}

.permission-title-row :deep(.v-list-item-title) {
  font-weight: 600;
  color: #1e293b;
}

.administrator-option,
.permission-switch-option {
  flex-shrink: 0;
  padding-left: 18px;
}

.administrator-option {
  border-left: 1px solid #d1fae5;
}

.permission-switch-option {
  min-width: 130px;
  border-left: 1px solid #e2e8f0;
}

.permission-switch-option :deep(.v-label) {
  min-width: 48px;
  color: #475569;
  font-size: 0.9rem;
}

.permission-roles-row {
  padding-top: 18px;
  margin-top: 14px !important;
  border-top: 1px solid #e2e8f0;
}

.cursor-pointer {
  cursor: pointer;
}

.v-data-table,
.v-data-table-server,
.v-table,
.v-table__wrapper,
.v-data-table__wrapper,
.v-data-table-header,
.v-data-footer {
  background: #ffffff !important;
  box-shadow: none !important;
}

.v-data-table th {
  font-weight: 600;
  color: #1e293b;
}

.v-data-table td {
  color: #475569;
}

@media (max-width: 700px) {
  .permission-header {
    align-items: flex-start;
    flex-direction: column;
    gap: 14px;
  }

  .administrator-option,
  .permission-switch-option {
    width: 100%;
    min-width: 0;
    padding-left: 0;
    padding-top: 12px;
    border-left: 0;
    border-top: 1px solid #e2e8f0;
  }

  .administrator-option {
    border-top-color: #d1fae5;
  }
}
</style>